'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Check, Sparkles, Zap, ChevronDown } from 'lucide-react';

const visiblePlans = [
  {
    id: 'free',
    name: 'Free',
    monthlyNOK: 0,
    annualNOK: 0,
    credits: 5,
    description: 'Try DESIGNHUB AI with no commitment.',
    badge: null,
    highlight: false,
    features: [
      '5 AI preview credits',
      'Limited templates',
      'Watermarked exports',
      '1 saved brand profile',
      'Basic design editor',
    ],
    cta: 'Start Free',
    ctaStyle: 'btn-secondary',
  },
  {
    id: 'personal',
    name: 'Personal',
    monthlyNOK: 149,
    annualNOK: 119,
    credits: 60,
    description: 'For individuals and personal projects.',
    badge: null,
    highlight: false,
    features: [
      '60 monthly AI credits',
      'Personal event templates',
      'High-quality image exports',
      'Basic video credits',
      '1 personal brand profile',
      'All personal categories',
    ],
    cta: 'Get Personal',
    ctaStyle: 'btn-secondary',
  },
  {
    id: 'business',
    name: 'Business',
    monthlyNOK: 399,
    annualNOK: 319,
    credits: 250,
    description: 'Complete creative suite for businesses.',
    badge: 'Most Popular',
    highlight: true,
    features: [
      '250 monthly AI credits',
      'Complete business catalog',
      'Brand kit & QR Studio',
      'Print-ready PDF export',
      'Social scheduling',
      '3 business profiles',
      'Campaign generator',
    ],
    cta: 'Get Business',
    ctaStyle: 'btn-primary',
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyNOK: 899,
    annualNOK: 719,
    credits: 700,
    description: 'Advanced tools for serious creators.',
    badge: null,
    highlight: false,
    features: [
      '700 monthly AI credits',
      '5 brand profiles',
      'Advanced video studio',
      'Team collaboration',
      'Priority generation',
      'Client approval links',
      'Analytics dashboard',
    ],
    cta: 'Get Pro',
    ctaStyle: 'btn-secondary',
  },
];

const hiddenPlans = [
  {
    id: 'creator',
    name: 'Creator',
    monthlyNOK: 249,
    annualNOK: 199,
    credits: 120,
    description: 'For content creators and social media.',
    badge: null,
    highlight: false,
    features: [
      '120 monthly AI credits',
      'YouTube & TikTok formats',
      'Short video tools',
      'Social scheduling',
      'Creator brand kit',
      'Reels & Story packs',
    ],
    cta: 'Get Creator',
    ctaStyle: 'btn-secondary',
  },
  {
    id: 'agency',
    name: 'Agency',
    monthlyNOK: 1499,
    annualNOK: 1199,
    credits: 1500,
    description: 'For agencies managing multiple clients.',
    badge: null,
    highlight: false,
    features: [
      '1,500 monthly AI credits',
      '20 client brands',
      'Agency dashboard',
      'Team roles & folders',
      'White-label review links',
      'Campaign duplication',
      'Priority support',
    ],
    cta: 'Get Agency',
    ctaStyle: 'btn-secondary',
  },
];

type Plan = typeof visiblePlans[0];

function PlanCard({ plan, annual }: { plan: Plan; annual: boolean }) {
  return (
    <div
      className={`relative rounded-3xl border p-6 flex flex-col transition-all duration-200 ${
        plan?.highlight
          ? 'border-primary/50 bg-card gold-glow scale-[1.02]'
          : 'border-border bg-card hover:border-primary/20'
      }`}
    >
      {plan?.badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-gradient-gold text-primary-foreground text-xs font-bold px-4 py-1 rounded-full flex items-center gap-1">
            <Zap size={11} />
            {plan?.badge}
          </span>
        </div>
      )}

      <div className="mb-4">
        <h3 className="text-lg font-bold text-foreground mb-1">{plan?.name}</h3>
        <p className="text-sm text-slate-300 leading-relaxed">{plan?.description}</p>
      </div>

      <div className="mb-4">
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-extrabold font-mono-nums text-foreground">
            {plan?.monthlyNOK === 0 ? 'Free' : `${annual ? plan?.annualNOK : plan?.monthlyNOK}`}
          </span>
          {plan?.monthlyNOK > 0 && (
            <span className="text-sm text-slate-300">NOK/mo</span>
          )}
        </div>
        {plan?.credits > 0 && (
          <div className="flex items-center gap-1 mt-1">
            <Sparkles size={11} className="text-primary" />
            <span className="text-xs font-medium text-primary">{plan?.credits?.toLocaleString()} AI credits/mo</span>
          </div>
        )}
      </div>

      <ul className="space-y-2 mb-6 flex-1">
        {plan?.features?.map((feature) => (
          <li key={`${plan?.id}-feat-${feature}`} className="flex items-start gap-2">
            <Check size={13} className="text-primary mt-0.5 flex-shrink-0" />
            <span className="text-sm text-slate-300 leading-relaxed">{feature}</span>
          </li>
        ))}
      </ul>

      <Link
        href="/sign-up-login-screen"
        className={`${plan?.ctaStyle} w-full text-center py-3 rounded-xl text-sm font-semibold`}
      >
        {plan?.cta}
      </Link>
    </div>
  );
}

export default function PricingSection() {
  const [annual, setAnnual] = useState(false);
  const [showAll, setShowAll] = useState(false);

  return (
    <section id="pricing" className="py-14 px-4 sm:px-6 lg:px-8 xl:px-10 2xl:px-16 max-w-screen-2xl mx-auto">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-widest mb-5">
          Pricing
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-3">
          Plans for Every Creator
        </h2>
        <p className="text-slate-300 text-base sm:text-lg max-w-xl mx-auto mb-7">
          Start free. Upgrade when you need more AI power. All plans in Norwegian Krone.
        </p>

        {/* Toggle */}
        <div className="inline-flex items-center gap-3 p-1 rounded-xl bg-surface-3 border border-border">
          <button
            onClick={() => setAnnual(false)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
              !annual ? 'bg-primary text-primary-foreground' : 'text-slate-300 hover:text-foreground'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setAnnual(true)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 ${
              annual ? 'bg-primary text-primary-foreground' : 'text-slate-300 hover:text-foreground'
            }`}
          >
            Annual
            <span className="text-xs font-bold text-green-400">-20%</span>
          </button>
        </div>
      </div>

      {/* Main 4 plans */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
        {visiblePlans?.map((plan) => (
          <PlanCard key={`plan-${plan?.id}`} plan={plan} annual={annual} />
        ))}
      </div>

      {/* View all plans toggle */}
      <div className="text-center mb-8">
        <button
          onClick={() => setShowAll(!showAll)}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border text-sm font-medium text-slate-300 hover:text-foreground hover:border-primary/30 transition-all"
        >
          {showAll ? 'Hide additional plans' : 'View all plans (Creator & Agency)'}
          <ChevronDown size={15} className={`transition-transform duration-200 ${showAll ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Hidden plans */}
      {showAll && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl mx-auto mb-8">
          {hiddenPlans?.map((plan) => (
            <PlanCard key={`plan-${plan?.id}`} plan={plan as Plan} annual={annual} />
          ))}
        </div>
      )}

      {/* Credit packs */}
      <div className="mt-4 p-5 sm:p-6 rounded-3xl bg-card border border-border">
        <div className="text-center mb-5">
          <h3 className="text-lg font-bold text-foreground mb-1">Need More Credits?</h3>
          <p className="text-sm text-slate-300">Buy one-time credit packs — they never expire.</p>
        </div>
        <div className="flex flex-wrap justify-center gap-4">
          {[
            { credits: 50, nok: 79 },
            { credits: 150, nok: 199 },
            { credits: 500, nok: 549 },
          ]?.map((pack) => (
            <div key={`pack-${pack?.credits}`} className="flex items-center gap-4 px-6 py-4 rounded-2xl bg-surface-3 border border-border">
              <div>
                <div className="flex items-center gap-1.5">
                  <Sparkles size={13} className="text-primary" />
                  <span className="font-bold text-foreground">{pack?.credits} Credits</span>
                </div>
                <span className="text-xs text-slate-300">One-time purchase</span>
              </div>
              <div className="text-right">
                <div className="font-bold text-foreground font-mono-nums">{pack?.nok} NOK</div>
                <Link href="/sign-up-login-screen" className="text-xs text-primary hover:underline">Buy now</Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}